import clsx from 'clsx';
import { ShieldAlert, ShieldOff } from 'lucide-react';

const REDACT_CATEGORY_LABELS: Record<string, string> = {
  system_prompt: 'system prompt',
  tool_name: 'tool name',
  cross_user_id: 'cross-user identifier',
  methodology: 'methodology',
  calibration_band: 'calibration band',
  semantic_rule: 'semantic rule',
};

export interface SecurityNoticeBlockProps {
  variant: 'block';
  /** Generic user-facing reason. NOT the underlying detection signals — those are forensic only. */
  reason: string;
}

export interface SecurityNoticeRedactProps {
  variant: 'redact';
  category:
    | 'system_prompt'
    | 'tool_name'
    | 'cross_user_id'
    | 'methodology'
    | 'calibration_band'
    | 'semantic_rule';
  stage: 'token' | 'semantic';
  /** The original span that was filtered. Surface in tooltip; do NOT render verbatim. */
  redactedSpan: string;
  replacement: string;
}

export type SecurityNoticeProps = SecurityNoticeBlockProps | SecurityNoticeRedactProps;

/**
 * Inline security notice rendered in the chat thread when the API SSE
 * controller emits a `security_block` (terminating) or `security_redact`
 * (non-terminating) event.
 *
 * Block variant: full-width inline banner with `bg-danger-dim border-danger/15
 * text-danger` (matches `<ToolErrorCallout>`'s styling family). Renders the
 * generic reason; signals are intentionally NOT shown to the user (forensic
 * detail only).
 *
 * Redact variant: subdued chip with the category label. Stage 1 (token)
 * indicates the original was substituted inline before reaching the user;
 * Stage 2 (semantic) indicates a post-stream methodology / calibration /
 * semantic-rule disclosure was caught after the message rendered. The full
 * `redactedSpan` lives in the tooltip for in-app debugging but is NOT
 * rendered as visible text — exposing it here would defeat the redaction.
 *
 * Ships with prompt-langgraph-optimization Phase 5 Session 2 (2026-05-03).
 */
export function SecurityNotice(props: SecurityNoticeProps) {
  if (props.variant === 'block') {
    return (
      <div
        role="alert"
        data-testid="security-notice-block"
        className="bg-danger-dim border border-danger/15 rounded-[3px] px-3 py-2 flex items-start gap-2"
      >
        <ShieldOff size={14} className="text-danger shrink-0 mt-0.5" aria-hidden="true" />
        <div className="flex flex-col gap-0.5 min-w-0">
          <span className="text-xs font-mono font-semibold text-danger tracking-[0.5px] uppercase">
            Message blocked
          </span>
          <span className="text-sm font-body text-text">{props.reason}</span>
        </div>
      </div>
    );
  }

  const categoryLabel = REDACT_CATEGORY_LABELS[props.category] ?? props.category;
  const stageLabel = props.stage === 'token' ? 'inline' : 'post-message';

  return (
    <span
      role="note"
      data-testid="security-notice-redact"
      data-category={props.category}
      data-stage={props.stage}
      title={`Filtered ${stageLabel} — category: ${categoryLabel}`}
      className={clsx(
        'inline-flex items-center gap-1 align-baseline',
        'px-1.5 py-0.5 rounded-[2px]',
        'bg-warn/10 border border-warn/20',
        'text-[10px] font-mono font-semibold text-warn tracking-[0.5px] uppercase',
        'cursor-help',
      )}
    >
      <ShieldAlert size={10} className="shrink-0" aria-hidden="true" />
      <span>redacted: {categoryLabel}</span>
    </span>
  );
}
